import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

// Converte una data ISO "YYYY-MM-DD" in formato italiano "dd/mm/yyyy"; ritorna "—" se vuota.
function fmtItDate(iso) {
  if (!iso) return "—";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

export default function MensaMesePeriodiPage() {
  const [periodi, setPeriodi] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wizard, setWizard] = useState(null); // { step, anno, mese, giorni, file }
  const navigate = useNavigate();

  const fetchPeriodi = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/mensa-mese/disponibili");
      setPeriodi(Array.isArray(r.data) ? r.data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPeriodi(); }, [fetchPeriodi]);

  const goToPeriodo = (p) => {
    navigate(`/g/mensa_mese/mensa_mese_alunni?anno=${p.anno}&mese=${p.mese}`);
  };

  const openWizard = () => {
    const today = new Date();
    const anno = today.getFullYear();
    const mese = today.getMonth() + 1;
    // Default: dal 1° del mese fino all'ultimo giorno del mese (formato YYYY-MM-DD)
    const pad = (n) => String(n).padStart(2, "0");
    const last = new Date(anno, mese, 0).getDate();
    setWizard({
      step: 1, anno, mese, giorni: 20, file: null,
      data_da: `${anno}-${pad(mese)}-01`,
      data_a: `${anno}-${pad(mese)}-${pad(last)}`,
    });
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#7e57c2", letterSpacing: 0.5, textTransform: "uppercase" }}>Mensa Mese</div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: "#4a148c" }}>📅 Periodi gestiti</h1>
        </div>
        <button onClick={openWizard} data-testid="mensa-periodi-nuovo-btn"
          style={{
            padding: "12px 22px",
            background: "linear-gradient(180deg, #6a1b9a 0%, #4a148c 100%)",
            color: "white", border: "2px solid #4a148c", fontSize: 14, fontWeight: 800,
            cursor: "pointer", letterSpacing: 0.5, textTransform: "uppercase",
            boxShadow: "0 3px 10px rgba(74,20,140,0.4)",
          }}>+ Crea nuovo mese</button>
      </div>

      {loading && <div style={{ padding: 30, textAlign: "center", color: "#666" }}>Caricamento...</div>}

      {!loading && periodi.length === 0 && (
        <div style={{ padding: 40, textAlign: "center", background: "#fffde7", border: "2px dashed #ffa726", color: "#e65100", fontWeight: 700 }}>
          Nessun mese ancora creato. Premi "Crea nuovo mese" per iniziare.
        </div>
      )}

      {!loading && periodi.length > 0 && (
        <div style={{ background: "white", border: "2px solid #6a1b9a" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="mensa-periodi-table">
            <thead>
              <tr style={{ background: "linear-gradient(180deg, #6a1b9a 0%, #4a148c 100%)", color: "white" }}>
                <th style={{ padding: "12px 14px", textAlign: "left", fontSize: 12, letterSpacing: 0.5, textTransform: "uppercase" }}>Periodo</th>
                <th style={{ padding: "12px 14px", textAlign: "left", fontSize: 12, letterSpacing: 0.5, textTransform: "uppercase" }}>AS</th>
                <th style={{ padding: "12px 14px", textAlign: "center", fontSize: 12, letterSpacing: 0.5, textTransform: "uppercase" }}>Anno ISEE</th>
                <th style={{ padding: "12px 14px", textAlign: "center", fontSize: 12, letterSpacing: 0.5, textTransform: "uppercase", background: "#fff3e0", color: "#e65100" }} title="Intervallo di date in cui sono state totalizzate le presenze in mensa per questo mese">Periodo presenze</th>
                <th style={{ padding: "12px 14px", textAlign: "center", fontSize: 12, letterSpacing: 0.5, textTransform: "uppercase" }}>Giorni</th>
                <th style={{ padding: "12px 14px", textAlign: "center", fontSize: 12, letterSpacing: 0.5, textTransform: "uppercase" }}>Alunni</th>
                <th style={{ padding: "12px 14px", textAlign: "center", fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", background: "#fce4ec", color: "#880e4f" }}>Pres. A<br/><span style={{ fontSize: 9, opacity: 0.75 }}>Infanzia</span></th>
                <th style={{ padding: "12px 14px", textAlign: "center", fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", background: "#e8f5e9", color: "#1b5e20" }}>Pres. E<br/><span style={{ fontSize: 9, opacity: 0.75 }}>Primaria E</span></th>
                <th style={{ padding: "12px 14px", textAlign: "center", fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", background: "#f1f8e9", color: "#33691e" }}>Pres. F<br/><span style={{ fontSize: 9, opacity: 0.75 }}>Primaria F</span></th>
                <th style={{ padding: "12px 14px", textAlign: "center", fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", background: "#e3f2fd", color: "#0d47a1" }}>Pres. M<br/><span style={{ fontSize: 9, opacity: 0.75 }}>Secondaria</span></th>
                <th style={{ padding: "12px 14px", textAlign: "center", fontSize: 12, letterSpacing: 0.5, textTransform: "uppercase", background: "#4a148c", color: "white" }}>Tot.<br/>Pres.</th>
                <th style={{ padding: "12px 14px", textAlign: "left", fontSize: 12, letterSpacing: 0.5, textTransform: "uppercase" }}>File</th>
                <th style={{ padding: "12px 14px", textAlign: "right", fontSize: 12, letterSpacing: 0.5, textTransform: "uppercase" }}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {periodi.map((p, i) => {
                const ps = p.presenze_per_settore || { A: 0, E: 0, F: 0, M: 0 };
                return (
                <tr key={`${p.anno}-${p.mese}`} data-testid={`mensa-periodi-row-${p.anno}-${p.mese}`}
                  style={{ borderBottom: "1px solid #eee", background: i % 2 ? "#fafafa" : "white", cursor: "pointer" }}
                  onClick={() => goToPeriodo(p)}>
                  <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 800, color: "#4a148c" }}>
                    📅 {MESI[p.mese - 1]} {p.anno}
                  </td>
                  <td style={{ padding: "12px 14px", fontFamily: "monospace", fontSize: 13, color: "#37474f" }}>
                    {p.anno_scolastico}/{String(p.anno_scolastico + 1).slice(2)}
                  </td>
                  <td style={{ padding: "12px 14px", textAlign: "center", fontFamily: "monospace", fontSize: 13, fontWeight: 800, color: "#00838f" }}>
                    {p.anno_isee || "—"}
                  </td>
                  <td style={{ padding: "12px 14px", textAlign: "center", fontFamily: "monospace", fontSize: 12, color: "#bf360c", background: "#fff3e0", fontWeight: 700 }}
                    data-testid={`mensa-periodi-periodo-${p.anno}-${p.mese}`}
                    title={p.data_da || p.data_a ? `Presenze totalizzate da ${fmtItDate(p.data_da)} al ${fmtItDate(p.data_a)}` : "Periodo non impostato — modifica nel dettaglio mese"}>
                    {p.data_da || p.data_a
                      ? <>{fmtItDate(p.data_da)}<br/><span style={{ fontSize: 10, opacity: 0.7 }}>→</span><br/>{fmtItDate(p.data_a)}</>
                      : <span style={{ color: "#999", fontStyle: "italic", fontWeight: 400 }}>—</span>}
                  </td>
                  <td style={{ padding: "12px 14px", textAlign: "center", fontSize: 14, fontWeight: 800, color: p.giorni_scuola > 0 ? "#1565c0" : "#c62828" }}>
                    {p.giorni_scuola || "—"}
                  </td>
                  <td style={{ padding: "12px 14px", textAlign: "center", fontSize: 14, fontWeight: 700, color: "#37474f" }}>
                    {p.alunni}
                  </td>
                  <td style={{ padding: "12px 14px", textAlign: "center", fontSize: 14, fontWeight: 700, color: "#880e4f", background: "#fce4ec" }} data-testid={`mensa-periodi-pres-A-${p.anno}-${p.mese}`}>
                    {ps.A || 0}
                  </td>
                  <td style={{ padding: "12px 14px", textAlign: "center", fontSize: 14, fontWeight: 700, color: "#1b5e20", background: "#e8f5e9" }} data-testid={`mensa-periodi-pres-E-${p.anno}-${p.mese}`}>
                    {ps.E || 0}
                  </td>
                  <td style={{ padding: "12px 14px", textAlign: "center", fontSize: 14, fontWeight: 700, color: "#33691e", background: "#f1f8e9" }} data-testid={`mensa-periodi-pres-F-${p.anno}-${p.mese}`}>
                    {ps.F || 0}
                  </td>
                  <td style={{ padding: "12px 14px", textAlign: "center", fontSize: 14, fontWeight: 700, color: "#0d47a1", background: "#e3f2fd" }} data-testid={`mensa-periodi-pres-M-${p.anno}-${p.mese}`}>
                    {ps.M || 0}
                  </td>
                  <td style={{ padding: "12px 14px", textAlign: "center", fontSize: 15, fontWeight: 800, color: "white", background: "#6a1b9a" }}>
                    {p.presenze}
                  </td>
                  <td style={{ padding: "12px 14px", fontFamily: "monospace", fontSize: 11, color: "#888" }}>
                    {p.filename || "—"}
                  </td>
                  <td style={{ padding: "12px 14px", textAlign: "right" }}>
                    <button onClick={(e) => { e.stopPropagation(); goToPeriodo(p); }} data-testid={`mensa-periodi-open-${p.anno}-${p.mese}`}
                      style={{ padding: "6px 14px", background: "#4a148c", color: "white", border: "none", fontSize: 12, fontWeight: 800, cursor: "pointer", letterSpacing: 0.5, textTransform: "uppercase" }}>
                      Apri →
                    </button>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}

      {wizard && <CreateMeseWizard wizard={wizard} setWizard={setWizard} onCreated={(anno, mese) => {
        setWizard(null);
        fetchPeriodi();
        navigate(`/g/mensa_mese/mensa_mese_alunni?anno=${anno}&mese=${mese}`);
      }} />}
    </div>
  );
}

function CreateMeseWizard({ wizard, setWizard, onCreated }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const fileRef = useRef(null);

  const next = () => setWizard({ ...wizard, step: wizard.step + 1 });
  const back = () => setWizard({ ...wizard, step: wizard.step - 1 });
  const close = () => setWizard(null);

  const saveAndUpload = async () => {
    setBusy(true); setErr(null);
    try {
      // 1) Salva giorni_scuola + intervallo date (data_da/data_a)
      await api.put(`/mensa-mese/giorni-scuola`, null, {
        params: {
          anno: wizard.anno,
          mese: wizard.mese,
          giorni: wizard.giorni,
          data_da: wizard.data_da || "",
          data_a: wizard.data_a || "",
        },
      });
      // 2) Carica file Excel presenze
      if (wizard.file) {
        const fd = new FormData();
        fd.append("file", wizard.file);
        fd.append("anno", String(wizard.anno));
        fd.append("mese", String(wizard.mese));
        await api.post(`/mensa-mese/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      onCreated(wizard.anno, wizard.mese);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div onClick={close} data-testid="mensa-periodi-wizard"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "white", width: "min(540px, 95vw)", boxShadow: "0 8px 30px rgba(0,0,0,0.4)", border: "2px solid #6a1b9a" }}>
        <div style={{ background: "linear-gradient(180deg, #6a1b9a 0%, #4a148c 100%)", color: "white", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.85, letterSpacing: 0.5, textTransform: "uppercase" }}>Mensa Mese · Passo {wizard.step} di 3</div>
            <div style={{ fontSize: 17, fontWeight: 800 }}>
              {wizard.step === 1 && "📅 Quale mese vuoi creare?"}
              {wizard.step === 2 && "🗓 Quanti giorni di scuola ha avuto?"}
              {wizard.step === 3 && "📤 Carica il file Excel presenze"}
            </div>
          </div>
          <button onClick={close} data-testid="mensa-periodi-wizard-close"
            style={{ background: "transparent", color: "white", border: "1px solid rgba(255,255,255,0.5)", padding: "4px 14px", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>✕</button>
        </div>

        <div style={{ padding: 24 }}>
          {wizard.step === 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#4a148c", marginBottom: 4, letterSpacing: 0.3, textTransform: "uppercase" }}>Mese</div>
                <select value={wizard.mese} onChange={e => {
                    const mese = Number(e.target.value);
                    const pad = (n) => String(n).padStart(2, "0");
                    const last = new Date(wizard.anno, mese, 0).getDate();
                    setWizard({ ...wizard, mese,
                      data_da: `${wizard.anno}-${pad(mese)}-01`,
                      data_a: `${wizard.anno}-${pad(mese)}-${pad(last)}`,
                    });
                  }} data-testid="wizard-mese"
                  style={{ width: "100%", padding: "10px 14px", fontSize: 15, fontWeight: 700, border: "2px solid #6a1b9a", color: "#4a148c" }}>
                  {MESI.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#4a148c", marginBottom: 4, letterSpacing: 0.3, textTransform: "uppercase" }}>Anno</div>
                <select value={wizard.anno} onChange={e => {
                    const anno = Number(e.target.value);
                    const pad = (n) => String(n).padStart(2, "0");
                    const last = new Date(anno, wizard.mese, 0).getDate();
                    setWizard({ ...wizard, anno,
                      data_da: `${anno}-${pad(wizard.mese)}-01`,
                      data_a: `${anno}-${pad(wizard.mese)}-${pad(last)}`,
                    });
                  }} data-testid="wizard-anno"
                  style={{ width: "100%", padding: "10px 14px", fontSize: 15, fontWeight: 700, border: "2px solid #6a1b9a", color: "#4a148c", fontFamily: "monospace" }}>
                  {[wizard.anno - 1, wizard.anno, wizard.anno + 1].map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div style={{ padding: 10, background: "#f3e5f5", border: "1px solid #ce93d8", fontSize: 12, color: "#4a148c" }}>
                Stai creando: <b>{MESI[wizard.mese - 1]} {wizard.anno}</b> · AS <b>{(wizard.mese < 9 ? wizard.anno - 1 : wizard.anno)}/{String((wizard.mese < 9 ? wizard.anno - 1 : wizard.anno) + 1).slice(2)}</b>
              </div>
            </div>
          )}

          {wizard.step === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#4a148c", marginBottom: 4, letterSpacing: 0.3, textTransform: "uppercase" }}>Giorni di scuola del mese</div>
                <input type="number" min="0" max="31" value={wizard.giorni}
                  onChange={e => setWizard({ ...wizard, giorni: Number(e.target.value) || 0 })}
                  data-testid="wizard-giorni"
                  style={{ width: "100%", padding: "12px 14px", fontSize: 24, fontWeight: 800, border: "2px solid #6a1b9a", color: "#4a148c", textAlign: "center", fontFamily: "monospace" }} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#e65100", marginBottom: 4, letterSpacing: 0.3, textTransform: "uppercase" }} title="Data iniziale per il conteggio presenze">📅 Presenze dal</div>
                  <input type="date" value={wizard.data_da || ""}
                    onChange={e => setWizard({ ...wizard, data_da: e.target.value })}
                    data-testid="wizard-data-da"
                    style={{ width: "100%", padding: "10px 12px", fontSize: 14, fontWeight: 700, border: "2px solid #ef6c00", color: "#bf360c", fontFamily: "monospace", background: "#fff3e0" }} />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#e65100", marginBottom: 4, letterSpacing: 0.3, textTransform: "uppercase" }} title="Data finale per il conteggio presenze">📅 Presenze al</div>
                  <input type="date" value={wizard.data_a || ""}
                    onChange={e => setWizard({ ...wizard, data_a: e.target.value })}
                    data-testid="wizard-data-a"
                    style={{ width: "100%", padding: "10px 12px", fontSize: 14, fontWeight: 700, border: "2px solid #ef6c00", color: "#bf360c", fontFamily: "monospace", background: "#fff3e0" }} />
                </div>
              </div>
              <div style={{ padding: 10, background: "#f3e5f5", border: "1px solid #ce93d8", fontSize: 12, color: "#4a148c" }}>
                Le <b>assenze</b> di ogni alunno saranno calcolate come <b>{wizard.giorni} - presenze</b>.
                {wizard.data_da && wizard.data_a && (
                  <> Il file Excel deve totalizzare le presenze nell'intervallo <b>{fmtItDate(wizard.data_da)} → {fmtItDate(wizard.data_a)}</b>.</>
                )}
              </div>
            </div>
          )}

          {wizard.step === 3 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#4a148c", marginBottom: 4, letterSpacing: 0.3, textTransform: "uppercase" }}>File Excel presenze</div>
                <input type="file" accept=".xls,.xlsx,.xml" ref={fileRef}
                  onChange={e => setWizard({ ...wizard, file: e.target.files?.[0] || null })}
                  data-testid="wizard-file"
                  style={{ width: "100%", padding: 10, fontSize: 13, border: "2px dashed #6a1b9a", color: "#4a148c", background: "#fafafa" }} />
                {wizard.file && (
                  <div style={{ marginTop: 8, padding: 8, background: "#e8f5e9", border: "1px solid #66bb6a", fontSize: 12, color: "#1b5e20", fontWeight: 700 }}>
                    📎 {wizard.file.name} · {Math.round(wizard.file.size / 1024)} KB
                  </div>
                )}
              </div>
              <div style={{ padding: 10, background: "#f3e5f5", border: "1px solid #ce93d8", fontSize: 12, color: "#4a148c" }}>
                Verrà associato a <b>{MESI[wizard.mese - 1]} {wizard.anno}</b> con <b>{wizard.giorni}</b> giorni scuola.
              </div>
              {err && <div style={{ padding: 10, background: "#ffebee", border: "1px solid #ef5350", color: "#c62828", fontSize: 12, fontWeight: 700 }}>⚠️ {err}</div>}
            </div>
          )}
        </div>

        <div style={{ padding: "12px 20px", borderTop: "1px solid #e0e0e0", display: "flex", justifyContent: "space-between", gap: 8, background: "#fafafa" }}>
          {wizard.step > 1
            ? <button onClick={back} disabled={busy} style={{ padding: "8px 18px", background: "white", color: "#666", border: "1px solid #999", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>← Indietro</button>
            : <button onClick={close} disabled={busy} style={{ padding: "8px 18px", background: "white", color: "#666", border: "1px solid #999", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Annulla</button>
          }
          {wizard.step < 3
            ? <button onClick={next} data-testid="wizard-next"
                style={{ padding: "8px 22px", background: "linear-gradient(180deg, #6a1b9a 0%, #4a148c 100%)", color: "white", border: "none", fontWeight: 800, fontSize: 13, cursor: "pointer", letterSpacing: 0.5 }}>Avanti →</button>
            : <button onClick={saveAndUpload} disabled={busy || !wizard.file} data-testid="wizard-finish"
                style={{ padding: "8px 22px", background: (busy || !wizard.file) ? "#90a4ae" : "linear-gradient(180deg, #2e7d32 0%, #1b5e20 100%)", color: "white", border: "none", fontWeight: 800, fontSize: 13, cursor: (busy || !wizard.file) ? "wait" : "pointer", letterSpacing: 0.5 }}>
                {busy ? "Importazione..." : "✓ Crea e importa"}
              </button>
          }
        </div>
      </div>
    </div>
  );
}
